import _styled, { default as _styled2, default as _styled3, default as _styled4 } from "styled-components";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Wrap } from "~/features/ui/wrap";
import { type SpecsRowSpecViewProps } from "~/features/blocks/inventory/specs-row/specs-row-spec";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type SpecsRowBlockViewProps<TSpec = Omit<SpecsRowSpecViewProps, "mode" | "theme">> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  getItemKey?: (spec: TSpec, index: number) => React.Key;
  hasTopDivider?: boolean | null;
  mode?: string | null;
  renderSpec: (spec: TSpec, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  specs?: TSpec[] | null;
  theme?: string | null;
};
export function SpecsRowBlockView<TSpec = Omit<SpecsRowSpecViewProps, "mode" | "theme">>({
  anchor,
  getItemKey,
  hasTopDivider = true,
  mode = "light",
  renderSpec,
  sectionProps,
  specs = [],
  theme = "white"
}: SpecsRowBlockViewProps<TSpec>) {
  const themeStyle = getThemeStyle({
    mode: mode as Mode,
    theme: theme as Theme
  });
  const items = specs ?? [];
  if (!hasItems(items)) {
    return null;
  }
  return <_StyledSection {...sectionProps} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px"
  }, themeStyle.border, themeStyle.background]}>
      <Wrap mode={mode as Mode}>
        <_StyledDiv>
          <_StyledUl $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "> :not([hidden]) ~ :not([hidden])": {
            "--tw-divide-y-reverse": "0",
            "borderTopWidth": "calc(1px * calc(1 - var(--tw-divide-y-reverse)))",
            "borderBottomWidth": "calc(1px * var(--tw-divide-y-reverse))"
          },
          "@media (min-width: 1024px)": {
            "display": "flex",
            "> :not([hidden]) ~ :not([hidden])": {
              "--tw-divide-x-reverse": "0",
              "borderRightWidth": "calc(1px * var(--tw-divide-x-reverse))",
              "borderLeftWidth": "calc(1px * calc(1 - var(--tw-divide-x-reverse)))",
              "--tw-divide-y-reverse": "0",
              "borderTopWidth": "calc(0px * calc(1 - var(--tw-divide-y-reverse)))",
              "borderBottomWidth": "calc(0px * var(--tw-divide-y-reverse))"
            },
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px"
          }
        }, themeStyle.background, themeStyle.border]}>
            {items.map((spec, index) => <_StyledLi key={getItemKey?.(spec, index) || index}>
                {renderSpec(spec, index)}
              </_StyledLi>)}
          </_StyledUl>
        </_StyledDiv>
      </Wrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-1syyh64-0"
})`${p => p.$_css}`;
var _StyledDiv = _styled2("div").withConfig({
  componentId: "sc-sf-1syyh64-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledUl = _styled3("ul").withConfig({
  componentId: "sc-sf-1syyh64-2"
})`${p => p.$_css2}`;
var _StyledLi = /*#__PURE__*/_styled4("li").withConfig({
  displayName: "specs-row___StyledLi",
  componentId: "sc-14euz5f-0"
})({
  "width": "100%"
});